import { useCallback, useState, type MouseEvent } from "react";
import {
  ContextMenu,
  type ContextMenuItem,
  type ContextMenuState,
} from "@/components/ui/ContextMenu";
import { PrintLocationDialog } from "@/features/timesheetFlow/components/PrintLocationDialog";
import {
  copyFileToClipboard,
  copyImageToClipboard,
  isImageContentType,
  resolveImageBlob,
  type CopyMediaSource,
} from "@/lib/clipboardMedia";
import { isElectronWithOpenTempFile, openFileInDefaultApp } from "@/lib/electron";
import { runPrintImageByLocationWithFeedback } from "@/lib/printJob";
import { toast } from "@/lib/toast";

export interface MaMediaContextSource extends CopyMediaSource {
  isImage?: boolean;
}

async function runCopy(
  fn: (source: CopyMediaSource) => Promise<void>,
  source: CopyMediaSource,
  successMessage: string,
): Promise<void> {
  try {
    await fn(source);
    toast.success(successMessage);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Kopieren fehlgeschlagen.";
    toast.error(msg);
  }
}

async function runOpenExternally(source: MaMediaContextSource): Promise<void> {
  try {
    const file = await resolveImageBlob(source);
    await openFileInDefaultApp(file, source.filename || file.name || "bild.jpg");
    if (isElectronWithOpenTempFile()) {
      toast.success("Bild in Standard-App geöffnet.");
    } else {
      toast.success("Bild wird heruntergeladen.");
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Öffnen fehlgeschlagen.";
    toast.error(msg);
  }
}

function buildMenuItems(
  source: MaMediaContextSource,
  onPrint: (source: MaMediaContextSource) => void,
): ContextMenuItem[] {
  const isImage =
    source.isImage ?? isImageContentType(source.contentType ?? source.file?.type);
  const items: ContextMenuItem[] = [];

  if (isImage) {
    items.push({
      id: "copy-image",
      label: "Bild kopieren",
      onClick: () =>
        void runCopy(
          copyImageToClipboard,
          source,
          "Bild als Dateianhang in Zwischenablage kopiert.",
        ),
    });
    items.push({
      id: "open-external",
      label: "In Standard-App öffnen",
      onClick: () => void runOpenExternally(source),
    });
    items.push({
      id: "print-location",
      label: "Am Standort drucken…",
      onClick: () => onPrint(source),
    });
    return items;
  }

  items.push({
    id: "copy-file",
    label: "Dokument kopieren",
    onClick: () =>
      void runCopy(
        copyFileToClipboard,
        source,
        "Dokument als Anhang in Zwischenablage kopiert.",
      ),
  });

  return items;
}

export function useMaMediaContextMenu() {
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [printSource, setPrintSource] = useState<MaMediaContextSource | null>(null);

  const openMediaContextMenu = useCallback(
    (e: MouseEvent, source: MaMediaContextSource) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        items: buildMenuItems(source, setPrintSource),
      });
    },
    [],
  );

  const handlePrint = useCallback(
    async (locationId: number, copies: number) => {
      if (!printSource) return;
      const file = await resolveImageBlob(printSource);
      await runPrintImageByLocationWithFeedback(locationId, copies, file, {
        filename: printSource.filename || file.name || "bild.jpg",
        loadingMessage: "Bild wird gedruckt…",
      });
    },
    [printSource],
  );

  const contextMenuNode = (
    <>
      <ContextMenu state={contextMenu} onClose={() => setContextMenu(null)} />
      <PrintLocationDialog
        open={!!printSource}
        onClose={() => setPrintSource(null)}
        onPrint={handlePrint}
        zIndexClass="z-[500]"
      />
    </>
  );

  return { openMediaContextMenu, contextMenuNode };
}
